import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save, Send, ArrowBack } from '@mui/icons-material';
import { Compass, CompassFormData } from '../types';
import { apiService } from '../services/api';
import CompassQuadrant from '../components/CompassQuadrant';

const CompassFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<CompassFormData>({
    title: '',
    what_happened: '',
    what_did_i_do: '',
    how_affected_others: '',
    what_can_i_do_better: '',
    what_happened_image: '',
    what_did_i_do_image: '',
    how_affected_others_image: '',
    what_can_i_do_better_image: '',
  });

  // Store temporarily uploaded images for new compasses
  const [tempImages, setTempImages] = useState<Record<string, string>>({});

  const isEditing = Boolean(id);

  useEffect(() => {
    if (isEditing && id) {
      loadCompass(id);
    }
  }, [id, isEditing]);

  const loadCompass = async (compassId: string) => {
    try {
      setLoading(true);
      const compass = await apiService.getCompass(compassId);
      setFormData({
        title: compass.title || '',
        what_happened: compass.what_happened || '',
        what_did_i_do: compass.what_did_i_do || '',
        how_affected_others: compass.how_affected_others || '',
        what_can_i_do_better: compass.what_can_i_do_better || '',
        what_happened_image: compass.what_happened_image || '',
        what_did_i_do_image: compass.what_did_i_do_image || '',
        how_affected_others_image: compass.how_affected_others_image || '',
        what_can_i_do_better_image: compass.what_can_i_do_better_image || '',
      });
    } catch (error) {
      setError('Failed to load compass');
      console.error('Failed to load compass:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CompassFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setError(null);
    setSuccess(null);
  };

  const handleTextChange = (field: keyof CompassFormData) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
    setSuccess(null);
  };

  const handleImageUpload = (quadrant: string) => async (file: File) => {
    try {
      if (isEditing && id) {
        // For existing compasses, upload directly
        const imageUrl = await apiService.uploadCompassImage(id, quadrant, file);
        const imageField = `${quadrant}_image` as keyof CompassFormData;
        setFormData(prev => ({
          ...prev,
          [imageField]: imageUrl,
        }));
      } else {
        // For new compasses, store as temporary base64 data URL
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          setTempImages(prev => ({
            ...prev,
            [quadrant]: dataUrl,
          }));
          const imageField = `${quadrant}_image` as keyof CompassFormData;
          setFormData(prev => ({
            ...prev,
            [imageField]: dataUrl,
          }));
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      throw error;
    }
  };

  const handleImageDelete = (quadrant: string) => async () => {
    try {
      if (isEditing && id) {
        // For existing compasses, delete from server
        await apiService.deleteCompassImage(id, quadrant);
      } else {
        // For new compasses, remove from temporary storage
        setTempImages(prev => {
          const updated = { ...prev };
          delete updated[quadrant];
          return updated;
        });
      }
      
      // Clear the image from form data in both cases
      const imageField = `${quadrant}_image` as keyof CompassFormData;
      setFormData(prev => ({
        ...prev,
        [imageField]: '',
      }));
    } catch (error) {
      console.error('Failed to delete image:', error);
      throw error;
    }
  };

  const handleSave = async (status: 'draft' | 'submitted') => {
    try {
      setSaving(true);
      setError(null);

      const compassData = {
        ...formData,
        status,
      };

      if (isEditing && id) {
        await apiService.updateCompass(id, compassData);
        setSuccess(`Compass ${status === 'draft' ? 'saved as draft' : 'submitted'} successfully!`);
      } else {
        const newCompass = await apiService.createCompass(compassData);
        setSuccess(`Compass ${status === 'draft' ? 'saved as draft' : 'submitted'} successfully!`);
        
        if (status === 'submitted') {
          setTimeout(() => navigate('/dashboard'), 2000);
        } else {
          setTimeout(() => navigate(`/compass/edit/${newCompass.id}`), 1000);
        }
      }
    } catch (error) {
      setError('Failed to save compass');
      console.error('Failed to save compass:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const questions = [
    {
      field: 'what_happened' as keyof CompassFormData,
      title: 'What happened?',
      placeholder: 'Describe the situation or event that occurred...',
      help: 'Be objective and factual. What was the context or trigger?',
    },
    {
      field: 'what_did_i_do' as keyof CompassFormData,
      title: 'What did I do or say?',
      placeholder: 'Describe your actions, words, or responses...',
      help: 'Focus on your specific behaviors, decisions, or reactions.',
    },
    {
      field: 'how_affected_others' as keyof CompassFormData,
      title: 'How did it affect others?',
      placeholder: 'Consider the impact on other people involved...',
      help: 'Think about emotional, practical, or relational effects on others.',
    },
    {
      field: 'what_can_i_do_better' as keyof CompassFormData,
      title: 'What can I do to make it better?',
      placeholder: 'What actions can you take moving forward...',
      help: 'Focus on concrete steps for improvement or making amends.',
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h4">
          {isEditing ? 'Edit Compass' : 'New Compass'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Grid container spacing={4}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title (optional)"
              placeholder="Give your compass a meaningful title..."
              value={formData.title}
              onChange={handleChange('title')}
              variant="outlined"
            />
          </Grid>

          {/* Four-quadrant questions with image support */}
          {questions.map((question, index) => {
            const imageField = `${question.field}_image` as keyof CompassFormData;
            return (
              <Grid item xs={12} md={6} key={question.field}>
                <CompassQuadrant
                  quadrant={question.field as 'what_happened' | 'what_did_i_do' | 'how_affected_others' | 'what_can_i_do_better'}
                  title={`${index + 1}. ${question.title}`}
                  helpText={question.help}
                  placeholder={question.placeholder}
                  textValue={formData[question.field] || ''}
                  imageUrl={formData[imageField] || ''}
                  onTextChange={handleTextChange(question.field)}
                  onImageUpload={handleImageUpload(question.field)}
                  onImageDelete={handleImageDelete(question.field)}
                  disabled={saving}
                  textRows={4}
                />
              </Grid>
            );
          })}

          {/* Action buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Save />}
                onClick={() => handleSave('draft')}
                disabled={saving}
              >
                Save as Draft
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={() => handleSave('submitted')}
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Submit'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CompassFormPage;
